import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import InventoryIcon from '@mui/icons-material/Inventory2';
import RestaurantIcon from '@mui/icons-material/RestaurantMenu';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import EventIcon from '@mui/icons-material/Event';
import PeopleIcon from '@mui/icons-material/People';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import BarChartIcon from '@mui/icons-material/BarChart';
import SettingsIcon from '@mui/icons-material/Settings';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAuthStore } from '@/app/authStore';

interface Props {
  drawerWidth: number;
  mobileOpen: boolean;
  onClose: () => void;
}

interface NavEntry {
  to: string;
  labelKey: string;
  icon: typeof DashboardIcon;
  permission?: string;
}

const ENTRIES: NavEntry[] = [
  { to: '/', labelKey: 'nav.dashboard', icon: DashboardIcon },
  { to: '/ingredients', labelKey: 'nav.ingredients', icon: InventoryIcon, permission: 'ingredient:read' },
  { to: '/dishes', labelKey: 'nav.dishes', icon: RestaurantIcon, permission: 'dish:read' },
  { to: '/menus', labelKey: 'nav.menus', icon: MenuBookIcon, permission: 'menu:read' },
  { to: '/orders', labelKey: 'nav.orders', icon: EventIcon, permission: 'order:read' },
  { to: '/clients', labelKey: 'nav.clients', icon: PeopleIcon, permission: 'client:read' },
  { to: '/procurement', labelKey: 'nav.procurement', icon: ShoppingCartIcon, permission: 'procurement:read' },
  { to: '/reports', labelKey: 'nav.reports', icon: BarChartIcon, permission: 'report:read' },
  { to: '/admin', labelKey: 'nav.admin', icon: SettingsIcon, permission: 'reference:read' },
];

export default function AppSidebar({ drawerWidth, mobileOpen, onClose }: Props) {
  const { t } = useTranslation();
  const can = useAuthStore((state) => state.can);
  const location = useLocation();

  const content = (
    <Box className="flex h-full flex-col">
      <Toolbar className="gap-2">
        <RestaurantIcon color="primary" />
        <Box>
          <Typography variant="h6" className="leading-tight">
            {t('app.title')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('app.subtitle')}
          </Typography>
        </Box>
      </Toolbar>

      <List className="flex-1 px-2">
        {ENTRIES.filter((entry) => !entry.permission || can(entry.permission)).map((entry) => {
          const Icon = entry.icon;
          const selected =
            entry.to === '/'
              ? location.pathname === '/'
              : location.pathname.startsWith(entry.to);
          return (
            <ListItemButton
              key={entry.to}
              component={NavLink}
              to={entry.to}
              selected={selected}
              onClick={onClose}
              className="rounded"
              sx={{ mb: 0.5 }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Icon fontSize="small" color={selected ? 'primary' : 'inherit'} />
              </ListItemIcon>
              <ListItemText
                primary={t(entry.labelKey)}
                primaryTypographyProps={{ fontSize: 14, fontWeight: selected ? 600 : 400 }}
              />
            </ListItemButton>
          );
        })}
      </List>
    </Box>
  );

  return (
    <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { width: drawerWidth, boxSizing: 'border-box' },
        }}
      >
        {content}
      </Drawer>
      <Drawer
        variant="permanent"
        open
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            borderRight: 1,
            borderColor: 'divider',
          },
        }}
      >
        {content}
      </Drawer>
    </Box>
  );
}
